import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaCalendarAlt, 
  FaUser, 
  FaHeart, 
  FaComment, 
  FaShareAlt, 
  FaBookMedical, 
  FaStethoscope, 
  FaHospital, 
  FaAppleAlt, 
  FaBrain,
  FaEye,
  FaArrowRight,
  FaSearch,
  FaFilter,
  FaTags,
  FaClock,
  FaBookReader,
  FaNewspaper,
  FaGraduationCap,
  FaTimes,
  FaBookmark
} from 'react-icons/fa';
import { 
  GiMedicinePills, 
  GiHealthPotion, 
  GiHeartBeats,
  GiHealthNormal,
  GiMedicines
} from 'react-icons/gi';
import { motion, AnimatePresence } from 'framer-motion';
import { useBlogStore } from '../Admin/AdminBlog';

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [likedPosts, setLikedPosts] = useState([]);
  const { categories: firebaseCategories, blogPosts: firebaseBlogPosts, authors } = useBlogStore();
  const [featuredPost, setFeaturedPost] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get categories with counts from Firebase
  const categoriesWithCounts = [
    { id: 'all', name: 'All Articles', icon: <FaNewspaper />, count: firebaseBlogPosts.length },
    ...firebaseCategories.map(category => ({
      ...category,
      count: firebaseBlogPosts.filter(post => post.category === category.id).length
    }))
  ];

  // Get popular tags from Firebase posts
  const getPopularTags = () => {
    const allTags = firebaseBlogPosts.flatMap(post => post.tags || []);
    const tagCounts = {};
    allTags.forEach(tag => {
      tagCounts[tag] = (tagCounts[tag] || 0) + 1;
    });
    return Object.entries(tagCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 12)
      .map(([tag]) => tag);
  };

  // Get featured authors from Firebase
  const getFeaturedAuthors = () => {
    const authorCounts = {};
    firebaseBlogPosts.forEach(post => {
      if (post.author) {
        authorCounts[post.author] = (authorCounts[post.author] || 0) + 1;
      }
    });
    
    return authors
      .filter(author => authorCounts[author.name] > 0)
      .map(author => ({
        ...author,
        articles: authorCounts[author.name] || 0
      }))
      .slice(0, 3);
  };

  const popularTags = getPopularTags();
  const featuredAuthors = getFeaturedAuthors();

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardHover = {
    hover: {
      y: -8,
      scale: 1.02,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15
      }
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.4, ease: "backOut" }
    }
  };

  // Set featured post
  useEffect(() => {
    const featured = firebaseBlogPosts.find(post => post.featured);
    setFeaturedPost(featured);
  }, [firebaseBlogPosts]);

  // Filter posts based on category and search
  const filteredPosts = firebaseBlogPosts.filter(post => {
    if (selectedCategory !== 'all' && post.category !== selectedCategory) return false;
    if (searchQuery && !post.title.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !(post.tags || []).some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))) return false;
    return true;
  });

  const handleLike = (postId) => {
    if (likedPosts.includes(postId)) {
      setLikedPosts(likedPosts.filter(id => id !== postId));
    } else {
      setLikedPosts([...likedPosts, postId]);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const openPostModal = (post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
  };

  const closePostModal = () => {
    setIsModalOpen(false);
    setSelectedPost(null);
  };

  const BlogPostModal = ({ post, isOpen, onClose }) => {
    if (!isOpen || !post) return null;

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-64 overflow-hidden">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <button
                onClick={onClose}
                className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm text-white p-2 rounded-full hover:bg-white/30 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
              <div className="absolute bottom-0 left-0 p-6">
                <span className="inline-block px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full mb-3">
                  {post.category.charAt(0).toUpperCase() + post.category.slice(1)}
                </span>
                <h2 className="text-3xl font-bold text-white mb-2">{post.title}</h2>
                <div className="flex items-center text-white/90 text-sm space-x-4">
                  <span className="flex items-center">
                    <FaUser className="mr-2" />
                    {post.author}
                  </span>
                  <span className="flex items-center">
                    <FaCalendarAlt className="mr-2" />
                    {formatDate(post.date)}
                  </span>
                  <span className="flex items-center">
                    <FaClock className="mr-2" />
                    {post.readTime || '5 min read'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="p-6 lg:p-8 overflow-y-auto max-h-[calc(90vh-16rem)]">
              <div className="prose prose-lg max-w-none">
                <p className="text-xl text-gray-600 leading-relaxed mb-6">{post.excerpt}</p>
                
                {post.content && (
                  <div className="text-gray-700 leading-relaxed space-y-4">
                    {post.content.split('\n\n').map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                )}
                
                {/* {!post.content && (
                  <div className="text-gray-700 leading-relaxed space-y-4">
                    <p>This is a comprehensive article about {post.title.toLowerCase()}. The full content would include detailed information, research findings, and practical advice on this important health topic.</p>
                    <p>In this article, we explore various aspects of {post.title.toLowerCase()}, including current research, expert opinions, and evidence-based recommendations for better health outcomes.</p>
                    <p>Stay tuned for more detailed insights and actionable tips that you can incorporate into your daily routine for improved wellness and disease prevention.</p>
                  </div>
                )} */}
              </div>
              
              {post.tags && post.tags.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-500 mb-3">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex space-x-6">
                    <button
                      onClick={() => handleLike(post.id)}
                      className={`flex items-center space-x-2 ${likedPosts.includes(post.id) ? 'text-red-500' : 'text-gray-500 hover:text-red-500'} transition-colors`}
                    >
                      <FaHeart className={likedPosts.includes(post.id) ? 'fill-current' : ''} />
                      <span>{likedPosts.includes(post.id) ? post.likes + 1 : post.likes}</span>
                    </button>
                    <span className="flex items-center space-x-2 text-gray-500">
                      <FaComment />
                      <span>{post.comments}</span>
                    </span>
                    <span className="flex items-center space-x-2 text-gray-500">
                      <FaShareAlt />
                      <span>{post.shares}</span>
                    </span>
                    <span className="flex items-center space-x-2 text-gray-500">
                      <FaEye />
                      <span>{post.views || 0}</span>
                    </span>
                  </div>
                  <button className="flex items-center space-x-2 text-teal-600 hover:text-teal-700 transition-colors">
                    <FaBookmark />
                    <span>Save</span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-r from-teal-200/20 to-emerald-200/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-r from-blue-200/20 to-purple-200/20 rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-teal-600 via-emerald-600 to-cyan-600"></div>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] opacity-10"></div>
        
        <div className="container mx-auto px-4 py-16 lg:py-24 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.div
              variants={scaleIn}
              className="inline-block mb-6"
            >
              <div className="bg-white/20 backdrop-blur-sm p-3 rounded-2xl inline-block">
                <FaBookMedical className="text-4xl text-white" />
              </div>
            </motion.div>
            
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Medical Insights &{' '}
              <span className="bg-gradient-to-r from-teal-300 to-emerald-300 bg-clip-text text-transparent">
                Health Wisdom
              </span>
            </h1>
            
            <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
              Evidence-based articles, research updates, and practical health advice from medical experts.
            </p>
            
            <motion.div
              variants={fadeInUp}
              transition={{ delay: 0.2 }}
              className="relative max-w-2xl mx-auto"
            >
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
              <input
                type="text"
                placeholder="Search for articles, health tips, or medical topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-teal-100 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
              <button 
                onClick={() => {}} 
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-6 py-2 rounded-xl hover:opacity-90 transition-opacity"
              >
                Search
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* Animated wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full h-16 lg:h-24">
            <path fill="#ffffff" fillOpacity="1" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 lg:py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content - 2/3 width */}
          <div className="lg:w-2/3">
            {/* Categories Filter */}
            <motion.div
              variants={fadeInUp}
              className="mb-12"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  Browse by Category
                </h2>
                <div className="flex items-center space-x-2 text-teal-600">
                  <FaFilter />
                  <span className="font-medium">Filter</span>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-3">
                {categoriesWithCounts.map((category) => (
                  <motion.button
                    key={category.id}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex items-center space-x-2 px-4 py-3 rounded-xl transition-all ${
                      selectedCategory === category.id
                        ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-lg'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200 hover:border-teal-300 hover:shadow-md'
                    }`}
                  >
                    <span>{category.icon}</span>
                    <span className="font-medium">{category.name}</span>
                    <span className={`text-sm px-2 py-1 rounded-full ${
                      selectedCategory === category.id 
                        ? 'bg-white/20' 
                        : 'bg-gray-100'
                    }`}>
                      {category.count}
                    </span>
                  </motion.button>
                ))}
              </div>
            </motion.div>

            {/* Blog Posts Grid */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 md:grid-cols-2 gap-8"
            >
              {filteredPosts.map((post) => (
                <motion.article
                  key={post.id}
                  variants={fadeInUp}
                  whileHover="hover"
                  className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-0 left-0 p-4">
                      <span className="inline-block px-3 py-1 text-xs font-semibold text-white bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full">
                        {post.category.charAt(0).toUpperCase() + post.category.slice(1)}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <span className="flex items-center mr-4">
                        <FaUser className="mr-1" />
                        {post.author}
                      </span>
                      <span className="flex items-center">
                        <FaCalendarAlt className="mr-1" />
                        {formatDate(post.date)}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4">{post.excerpt}</p>
                    <div className="flex justify-between items-center">
                      <div className="flex space-x-4 text-sm text-gray-500">
                        <button
                          onClick={() => handleLike(post.id)}
                          className={`flex items-center ${likedPosts.includes(post.id) ? 'text-red-500' : 'text-gray-500 hover:text-red-500'}`}
                        >
                          <FaHeart className="mr-1" />
                          {likedPosts.includes(post.id) ? post.likes + 1 : post.likes}
                        </button>
                        <span className="flex items-center">
                          <FaComment className="mr-1" />
                          {post.comments}
                        </span>
                        <span className="flex items-center">
                          <FaShareAlt className="mr-1" />
                          {post.shares}
                        </span>
                      </div>
                      <button
                        onClick={() => openPostModal(post)}
                        className="text-teal-600 hover:text-teal-700 font-medium flex items-center"
                      >
                        Read More <FaArrowRight className="ml-1" />
                      </button>
                    </div>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          </div>

          {/* Sidebar - 1/3 width */}
          <div className="lg:w-1/3 space-y-8">
            {/* About Card */}
            <motion.div
              variants={fadeInUp}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 shadow-md"
            >
              <h3 className="text-xl font-bold text-gray-800 mb-4">About Our Blog</h3>
              <p className="text-gray-600 mb-4">
                Welcome to our health and wellness blog, where we share evidence-based articles, expert insights, and practical advice to help you live a healthier life.
              </p>
              <button className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-2 px-4 rounded-lg hover:opacity-90 transition-opacity">
                Learn More
              </button>
            </motion.div>

            {/* Popular Tags */}
            <motion.div
              variants={fadeInUp}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl p-6 shadow-md"
            >
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <FaTags className="mr-2 text-teal-500" /> Popular Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {popularTags.map((tag, index) => (
                  <button
                    key={index}
                    onClick={() => setSearchQuery(tag)}
                    className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-full transition-colors"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Featured Authors */}
            <motion.div
              variants={fadeInUp}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-2xl p-6 shadow-md"
            >
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <FaUser className="mr-2 text-teal-500" /> Featured Authors
              </h3>
              <div className="space-y-4">
                {featuredAuthors.map((author) => (
                  <div key={author.id} className="flex items-center">
                    <img
                      src={author.image}
                      alt={author.name}
                      className="w-12 h-12 rounded-full object-cover mr-3"
                    />
                    <div>
                      <h4 className="font-medium text-gray-800">{author.name}</h4>
                      <p className="text-sm text-gray-500">{author.role}</p>
                      <p className="text-xs text-teal-600">{author.articles} articles</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* Blog Post Modal */}
      <BlogPostModal 
        post={selectedPost} 
        isOpen={isModalOpen} 
        onClose={closePostModal} 
      />
    </div>
  );
};

export default BlogPage;